import { Box, CircularProgress } from '@mui/material';
import { AppLayout } from 'components/Layouts/AppLayout';
import { VideoNotificationsProvider } from 'components/Notifications/VideoNotificationsProvider';
import { resolveBackgroundLocation } from 'components/pages/VideoManager/utils';
import { videoManagerRoute } from 'components/pages/VideoManager/VideoManagerRoute';
import { useAppSessionLocale } from 'hooks/useAppSessionLocale';
import { useRedirectUserThemePartner } from 'hooks/useRedirectUserThemePartner';
import { useShouldShowFirstBusinessPrompt } from 'hooks/useShouldShowFirstBusinessPrompt';
import { useUtilsBoot } from 'hooks/useUtilsBoot';
import { CampaignProvider } from 'pages/Campaign/CampaignContext/CampaignProvider';
import { FC, lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { PermissionsViewDecider } from 'shared/PermissionsViewDecider';
import { ReferralAnnouncement } from 'shared/ReferralAnnouncement';
import { WalletDialog } from 'shared/Wallet/WalletDialog';
import { APIPermission } from 'typeDeclarations/enums';

const LazyExpiredLinkPage = lazy(() =>
  import('pages/ExpiredLink').then(({ ExpiredLink }) => ({ default: ExpiredLink })),
);

const LazyFirstBusinessPage = lazy(() =>
  import('pages/FirstBusiness/FirstBusinessPage').then(({ FirstBusinessPage }) => ({
    default: FirstBusinessPage,
  })),
);

const LazyAllCampaignsPage = lazy(() =>
  import('pages/AdvertisersAndCampaigns/Campaigns/CampaignsPage/CampaignsPage').then(({ CampaignsPage }) => ({
    default: CampaignsPage,
  })),
);

const LazyGeneralReportsPage = lazy(() =>
  import('pages/GeneralReports/ReportsPage').then(({ ReportsPage }) => ({
    default: ReportsPage,
  })),
);

const LazyPulseCheckPage = lazy(() =>
  import('pages/PulseCheck/PulseCheckPage').then(({ PulseCheckPage }) => ({
    default: PulseCheckPage,
  })),
);

const LazyAllAdvertisersPage = lazy(() =>
  import('pages/AdvertisersAndCampaigns/Advertisers/AdvertisersPage/AdvertisersPage').then(({ AdvertisersPage }) => ({
    default: AdvertisersPage,
  })),
);

const LazyAdvertiserAdvancedOptionsPage = lazy(
  () => import('pages/AdvertisersAndCampaigns/Advertisers/AdvertiserAdvancedOptionsPage/AdvertiserAdvancedOptionsPage'),
);

const LazyAccountSettingsPage = lazy(() =>
  import('pages/AccountSettings/AccountSettings').then(({ AccountSettingsPage }) => ({ default: AccountSettingsPage })),
);

const LazyTeamManagementPage = lazy(() =>
  import('pages/TeamManagement/TeamManagement').then(({ TeamManagementPage }) => ({ default: TeamManagementPage })),
);

const LazyTeamDashboard = lazy(() =>
  import('pages/TeamDashboard/TeamDashboard').then(({ TeamDashboard }) => ({ default: TeamDashboard })),
);

const LazyCampaignPage = lazy(() =>
  import('pages/Campaign/Campaign').then(({ CampaignPage }) => ({ default: CampaignPage })),
);

const LazyBudgetEstimatorPage = lazy(() =>
  import('pages/BudgetEstimator/BudgetEstimator').then(({ BudgetEstimator }) => ({ default: BudgetEstimator })),
);

const LazyCampaignDashboardReport = lazy(() =>
  import('components/print/CampaignDashboard/CampaignDashboardReport').then(({ CampaignDashboardReport }) => ({
    default: CampaignDashboardReport,
  })),
);

const LazyCampaignSummaryPrint = lazy(() =>
  import('components/print/CampaignSummary/CampaignSummaryPrint').then(({ CampaignSummaryPrint }) => ({
    default: CampaignSummaryPrint,
  })),
);

const Loading: FC = () => (
  <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100vh">
    <CircularProgress size="5rem" thickness={2.5} />
  </Box>
);

export const ProtectedRoutes: FC = () => {
  // TODO(react-router): Should these initializations and checks be moved from this component to another place???
  useUtilsBoot();

  const { loading: useRedirectUserThemePartnerLoading, error: useRedirectUserThemePartnerError } =
    useRedirectUserThemePartner();

  const { loading: shouldShowFirstBusinessLoading, error: shouldShowFirstBusinessError } =
    useShouldShowFirstBusinessPrompt();

  const { loading: gettingAppLocale } = useAppSessionLocale();

  const location = useLocation(); // keep this with the other hooks, above the early returns
  const backgroundLocation = resolveBackgroundLocation(location); // router state, or ?from= for typed URLs

  if (useRedirectUserThemePartnerLoading || shouldShowFirstBusinessLoading || gettingAppLocale) return <Loading />;

  if (useRedirectUserThemePartnerError) throw useRedirectUserThemePartnerError;
  if (shouldShowFirstBusinessError) throw shouldShowFirstBusinessError;

  // TODO(react-router): Some of these routes should have permissions set.
  //
  // If the user doesn't have a business:
  // - They can only access /first-business.
  //
  // If the user has a business:
  // - They can access *every* page, except /first-business.
  //
  // Exception:
  // - /team/dashboard/* can only be accessible if the user has a minimum of 2 running campaigns. This can be backed up
  // by looking at the code found on `CampaignsPage.tsx`.
  //
  // Also, the /first-business assertion is already done via a custom hook, but we could move it here now.
  const content = (
    <>
      <Suspense fallback={<Loading />}>
        <Routes location={backgroundLocation ?? location}>
          <Route
            path="/print/campaign-dashboard/:campaignPrettyId"
            element={
              <CampaignProvider>
                <LazyCampaignDashboardReport />
              </CampaignProvider>
            }
          />
          <Route
            path="/print/campaign-summary/:campaignPrettyId"
            element={
              <CampaignProvider>
                <LazyCampaignSummaryPrint />
              </CampaignProvider>
            }
          />
          <Route element={<AppLayout />}>
            <Route path="/first-business" element={<LazyFirstBusinessPage />} />
            <Route path="/link-has-expired" element={<LazyExpiredLinkPage />} />
            <Route path="/pulse-check">
              <Route index element={<Navigate to="/pulse-check/network-problems" replace />} />
              <Route path="*" element={<LazyPulseCheckPage />} />
            </Route>
            <Route path="/businesses" element={<LazyAllAdvertisersPage />} />
            <Route path="/businesses/:advertiserPrettyId/advanced-options">
              <Route index path="*" element={<LazyAdvertiserAdvancedOptionsPage />} />
            </Route>
            <Route path="/campaigns" element={<LazyAllCampaignsPage />} />
            <Route path="/partner/reports" element={<LazyGeneralReportsPage />} />
            <Route path="/campaign/:campaignPrettyId">
              <Route index path="*" element={<LazyCampaignPage />} />
            </Route>
            <Route path="/team">
              <Route index element={<Navigate to="/team/dashboard/overview" replace />} />
              <Route path="billing">
                <Route index path="*" element={<LazyTeamManagementPage />} />
              </Route>
              <Route path="dashboard">
                <Route index path="*" element={<LazyTeamDashboard />} />
              </Route>
            </Route>
            <Route path="/account-settings">
              <Route index path="*" element={<LazyAccountSettingsPage />} />
            </Route>
            <Route path="/budget-estimator">
              <Route
                index
                path="*"
                element={
                  <PermissionsViewDecider
                    fallback={<Navigate to="/not-found" replace />}
                    requiredPermissions={[APIPermission.EstimatedCostPerGoalWithUrl]}
                  >
                    <LazyBudgetEstimatorPage />
                  </PermissionsViewDecider>
                }
              />
            </Route>
            {videoManagerRoute}
          </Route>
          <Route path="*" element={<Navigate to={'/not-found'} replace />} />
        </Routes>
        {backgroundLocation && (
          <Suspense fallback={null}>
            <Routes>{videoManagerRoute}</Routes>
          </Suspense>
        )}
      </Suspense>
      <ReferralAnnouncement />
      <WalletDialog />
    </>
  );

  // VideoNotificationsProvider wraps both <Routes>: the Video manager modal's own <Routes> renders outside AppLayout.
  // Print pages are left out, so a notification toast never ends up in a printout.
  const isPrintPage = location.pathname.startsWith('/print/');

  return isPrintPage ? content : <VideoNotificationsProvider>{content}</VideoNotificationsProvider>;
};
